import { Etapa } from "src/app/shared/model/gestion-proyectos/etapa/etapa";
import { MAXIMO_PROYECTOS_RESUMEN, ResumenEtapa, ResumenProyectoEtapas } from "../proyecto-pagina/etapas-versiones.model";
import {
  ChipEstado,
  chipsEstadoEtapa,
  claveEtapa,
  diaDeFechaDeEtapa,
  fechasDeEtapa,
  textoEsperanVersion,
  tituloFueraDeFecha,
  versionesRecientesPrimero,
} from "../proyecto-pagina/etapas-versiones.util";
import { TextoUltimaPrueba, textoUltimaPrueba } from "../proyecto-pagina/pruebas-version.util";

/*
 * La tabla compacta de etapas de cada tarjeta de Gestión de Proyectos (A0b;
 * plan docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8, maqueta
 * A0b-Proyectos-etapas-tabla.dc.html). Funciones puras: no recalculan reglas.
 *
 * - Con el resumen del servidor (PROYECTOS_ETAPAS_RESUMEN) cada renglón trae
 *   el estado, la vigente, la última prueba (Fase 4), las abiertas y si se
 *   ofrece «Crear versión».
 * - Mientras llega, o si no se pudo leer, la tabla sale con lo que ya trae el
 *   proyecto de la lista: nombre, fechas y vigente; Estado, Última prueba y
 *   Abiertas dicen «—» y no hay «Crear versión».
 * - Los ids de la página se piden en lotes de 50 (el tope del endpoint).
 * - T3b (Fase 3): si la etapa tiene resoluciones que esperan versión, «N
 *   resoluciones esperan versión» bajo la vigente y «Nueva versión» con
 *   permiso (plan §5.6); se entregan con o sin bandera.
 */

/** Una etapa en la tabla de la tarjeta, ya con sus textos. */
export interface FilaEtapaTarjeta {
  /** ?etapa= de la página del proyecto: el _id de la etapa o «pos-N». */
  clave: string;
  /** El _id de la etapa; null si se guardó sin él. */
  idEtapa: string | null;
  nombre: string;
  /** «09/07/2026 - 31/07/2026». */
  fechas: string;
  /** El estado y, aparte, «Fuera de fecha»; vacío mientras no hay resumen (la celda dice «—»). */
  chips: ChipEstado[];
  /** El title de «Fuera de fecha»: cuándo terminó y cuántas abiertas tiene. */
  tituloFueraDeFecha: string | null;
  /** «Día 13 de 31», «Inicia el …», «Terminó el …»; null fuera de fecha (lo dice el chip). */
  detalleEstado: string | null;
  /**
   * El nombre de la vigente (de cualquier ambiente), «Sin nombre» si se
   * guardó sin él; null = la etapa no tiene versiones («Sin versión vigente»).
   */
  vigente: string | null;
  /** Bajo la vigente: «QA · 3 versiones». */
  lineaVigente: string | null;
  /** «Regresión 2 · 14/08/2026», «115/120» y el chip del resultado; null sin ejecuciones o sin resumen («—»). */
  ultimaPrueba: TextoUltimaPrueba | null;
  /** D1; null mientras no hay resumen (la celda dice «—»). */
  abiertas: number | null;
  /** «Crear versión»: sin vigente, sin Terminar y con puedeCrearVersion (§5.1, D4 y D8). */
  crearVersion: boolean;
  /** «3 resoluciones esperan versión» (T3b); null sin pendientes o mientras no hay resumen. */
  esperanVersion: string | null;
  /** «Nueva versión» junto a la vigente: hay pendientes que entregar y permiso (D4 y D8). */
  nuevaVersion: boolean;
}

/**
 * Los renglones de la tabla: los del resumen del servidor, en su orden (D12);
 * sin resumen, los de la lista, ordenados igual (por fecha de inicio, las que
 * no tienen fecha al final y, en un empate, el orden de captura).
 */
export function filasEtapasTarjeta(
  etapasDeLaLista: readonly Etapa[] | null | undefined,
  resumen: ResumenProyectoEtapas | null | undefined,
): FilaEtapaTarjeta[] {
  if (resumen) {
    return (resumen.etapas ?? []).map((etapa, indice) => filaDelResumen(etapa, indice));
  }
  return etapasEnOrden(etapasDeLaLista).map((etapa, indice) => filaDeLaLista(etapa, indice));
}

/** «QA · 3 versiones», «1 versión» (sin ambiente) o null sin versiones. */
export function lineaVersionVigente(ambiente: string | null | undefined, totalVersiones: number | null | undefined): string | null {
  if (!totalVersiones || totalVersiones < 0) {
    return null;
  }
  const versiones = `${totalVersiones} ${totalVersiones === 1 ? "versión" : "versiones"}`;
  const enAmbiente = (ambiente ?? "").trim();
  return enAmbiente ? `${enAmbiente} · ${versiones}` : versiones;
}

/**
 * Los ids de los proyectos de la página en lotes para PROYECTOS_ETAPAS_RESUMEN:
 * recortados, sin vacíos ni repetidos y de 50 en 50 (el tope del endpoint).
 */
export function lotesDeIds(
  ids: readonly (string | number | null | undefined)[] | null | undefined,
  tamano = MAXIMO_PROYECTOS_RESUMEN,
): string[][] {
  const unicos = Array.from(new Set((ids ?? []).map((id) => String(id ?? "").trim()).filter(Boolean)));
  const porLote = Math.max(1, Math.floor(tamano));
  const lotes: string[][] = [];
  for (let inicio = 0; inicio < unicos.length; inicio += porLote) {
    lotes.push(unicos.slice(inicio, inicio + porLote));
  }
  return lotes;
}

/* ============================ Internas ============================ */

/** Lo que se pinta de una etapa o una versión que se guardó sin nombre. */
const SIN_NOMBRE = "Sin nombre";

/*
 * La vigente se decide porque exista (la de mayor orden, como el servidor),
 * no por su nombre: una versión vieja guardada sin nombre sigue siendo la
 * vigente y la etapa no ofrece «Crear versión».
 */

function filaDelResumen(etapa: ResumenEtapa, indice: number): FilaEtapaTarjeta {
  const chips = chipsEstadoEtapa(etapa.estado);
  const fuera = chips.length > 1;
  const vigente = etapa.vigente;
  return {
    clave: claveEtapa(etapa, indice),
    idEtapa: etapa.idEtapa?.trim() || null,
    nombre: etapa.nombre?.trim() || SIN_NOMBRE,
    fechas: fechasDeEtapa(etapa),
    chips,
    tituloFueraDeFecha: fuera ? tituloFueraDeFecha(etapa.fechaFin, etapa.abiertas) : null,
    detalleEstado: fuera ? null : chips[0].detalle,
    vigente: nombreDeVigente(vigente),
    lineaVigente: vigente ? lineaVersionVigente(vigente.ambiente, etapa.totalVersiones) : null,
    ultimaPrueba: textoUltimaPrueba(etapa.ultimaPrueba),
    abiertas: etapa.abiertas,
    crearVersion: !vigente && etapa.estado?.codigo !== "TERMINADA" && etapa.puedeCrearVersion && !!etapa.idEtapa,
    esperanVersion: textoEsperanVersion(etapa.pendientesVersion),
    nuevaVersion: !!vigente && etapa.pendientesVersion > 0 && etapa.puedeCrearVersion && !!etapa.idEtapa,
  };
}

function filaDeLaLista(etapa: Etapa, indice: number): FilaEtapaTarjeta {
  const versiones = (etapa.versiones ?? []).filter(Boolean);
  const vigente = versionesRecientesPrimero(versiones)[0] ?? null;
  const idEtapa = etapa.id?.trim() || null;
  return {
    clave: claveEtapa({ idEtapa }, indice),
    idEtapa,
    nombre: etapa.nombre?.trim() || SIN_NOMBRE,
    fechas: fechasDeEtapa(etapa),
    chips: [],
    tituloFueraDeFecha: null,
    detalleEstado: null,
    vigente: nombreDeVigente(vigente),
    lineaVigente: vigente ? lineaVersionVigente(vigente.ambiente, versiones.length) : null,
    ultimaPrueba: null,
    abiertas: null,
    crearVersion: false,
    esperanVersion: null,
    nuevaVersion: false,
  };
}

/** El nombre de la vigente; «Sin nombre» si se guardó vacío y null si no hay vigente. */
function nombreDeVigente(vigente: { nombre?: string | null } | null): string | null {
  if (!vigente) {
    return null;
  }
  return vigente.nombre?.trim() || SIN_NOMBRE;
}

/** D12 en el front, como ConsultaEtapasVersionesBuss.enOrden: por fecha de inicio y sin entradas nulas. */
function etapasEnOrden(etapas: readonly Etapa[] | null | undefined): Etapa[] {
  return (etapas ?? [])
    .filter(Boolean)
    .map((etapa) => ({ etapa, inicio: diaDeFechaDeEtapa(etapa.fechaInicio) }))
    .sort((a, b) => compararInicio(a.inicio, b.inicio))
    .map((fila) => fila.etapa);
}

function compararInicio(a: string | null, b: string | null): number {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return a < b ? -1 : 1;
}
